// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useMemo } from 'react';

import { Box, Divider, Flex, Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import {
  type EntityStatusBadgeProps,
  EntityStatusBadge,
  type GetLabelAndColorArgs,
} from 'components/Mantine/EntityStatusBadge';
import type { BadgeSize } from 'components/Mantine/StatusBadge';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { useHasAccess } from 'hooks/user/useHasAccess';

import { ManageStatusesModal } from './ManageStatusesModal';
import { StatusPickerContents } from './StatusPickerContents';
import type { CustomStatusEntityName, StateNameFor, StatusIdFor } from './types';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  stateName: StateNameFor<TEntity>;
  statusId: StatusIdFor<TEntity> | null;
  editable: boolean;
  /** If true, will hide the "Manage statuses" button (even if the user has access). */
  hideStatusManagementModal?: boolean;
  /** Called when the user assigns a status (or clears it via `null`). */
  onAssignStatus: (id: StatusIdFor<TEntity> | null) => Promise<void>;
} & Omit<EntityStatusBadgeProps<TEntity>, keyof GetLabelAndColorArgs<TEntity>>;

const sizeToIconMap: Record<BadgeSize, `${number}rem`> = {
  xs: '0.375rem',
  sm: '0.500rem',
  md: '0.625rem',
  lg: '0.750rem',
  xl: '0.750rem',
  xxl: '0.750rem',
};

/**
 * The clickable badge + picker + manage-statuses-modal for an entity instance.
 */
export const EditableStatusBadge = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  stateName,
  statusId,
  editable: initialEditable,
  hideStatusManagementModal,
  onAssignStatus,
  size = 'lg',
  ...props
}: Props<TEntity>) => {
  const { statesWithStatuses } = useCustomStatuses(customStatusTypeName);
  const [pickerOpened, { open: openPicker, close: closePicker, toggle: togglePicker }] = useDisclosure(false);
  const [manageOpened, { open: openManage, close: closeManage }] = useDisclosure(false);

  const editable = useMemo(
    () => initialEditable && statesWithStatuses.includes(stateName),
    [initialEditable, statesWithStatuses, stateName],
  );

  const [assigning, setAssigning] = useState(false);
  const editing = pickerOpened || manageOpened;

  const assignStatus = async (newStatusId: StatusIdFor<TEntity> | null) => {
    if (newStatusId === statusId) return;
    setAssigning(true);
    try {
      await onAssignStatus(newStatusId);
      closePicker();
    } finally {
      setAssigning(false);
    }
  };

  const hasAccessToEditCustomStatuses = useHasAccess('customStatus', 'write');
  const canManage = hasAccessToEditCustomStatuses && !hideStatusManagementModal && statesWithStatuses.length > 0;

  return (
    <>
      <Popover position="bottom-end" opened={pickerOpened} onChange={assigning ? undefined : togglePicker}>
        <Popover.Target>
          <EntityStatusBadge
            customStatusEntityName={customStatusTypeName}
            stateName={stateName}
            statusId={statusId}
            rightSection={
              editable && (
                <Flex align="center" justify="center">
                  <Divider orientation="vertical" my={-20} mx={5} color="currentColor" />
                  <Box pl={editing ? 0 : 5} pr={editing ? 5 : 0} style={{ rotate: editing ? '180deg' : undefined }}>
                    <EvolveIcon icon="ArrowDown" size={sizeToIconMap[size]} />
                  </Box>
                </Flex>
              )
            }
            size={size}
            style={{ cursor: editable ? 'pointer' : 'initial' }}
            onClick={() => {
              if (!editable || assigning) return;
              if (pickerOpened) closePicker();
              else openPicker();
            }}
            {...props}
          />
        </Popover.Target>
        <Popover.Dropdown p={0} miw={200} onClick={(e) => e.stopPropagation()}>
          <StatusPickerContents
            customStatusTypeName={customStatusTypeName}
            currentStatusId={statusId}
            stateName={stateName}
            onOpenManage={
              canManage
                ? () => {
                    openManage();
                    closePicker();
                  }
                : undefined
            }
            onAssign={assignStatus}
          />
        </Popover.Dropdown>
      </Popover>

      {canManage && (
        <ManageStatusesModal
          customStatusTypeName={customStatusTypeName}
          currentStateName={stateName}
          opened={manageOpened}
          onClose={() => {
            closeManage();
            openPicker();
          }}
        />
      )}
    </>
  );
};
